import React from 'react';
import { FiSearch,  FiCheckCircle, FiClock, FiBarChart2, FiMapPin, FiMonitor } from 'react-icons/fi';

const EducationalSEO = () => {
  const seoSteps = [
    {
      icon: <FiSearch className="w-6 h-6" />,
      title: 'Step 1: Research What Parents Are Searching For',
      description: 'Start by understanding what parents and students type into Google when looking for institutions like yours. Common searches include "best schools in [city]," "top PU colleges near me," "degree college admissions [year]," or "coaching for JEE in [location]." Use these keywords naturally on your website.',
      stat: '70% of parents search within their city or locality',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiMapPin className="w-6 h-6" />,
      title: 'Step 2: Optimize for Local Search',
      description: 'Google values local relevance. Include your city, locality, and landmark names on your website. Create a Google My Business profile. Encourage parents to leave reviews. All this helps you rank for "near me" searches.',
      stat: '80% of local searches lead to a conversion',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiMonitor className="w-6 h-6" />,
      title: 'Step 3: Create Program-Specific Pages',
      description: 'Don\'t just have one page for "Admissions." Create separate pages for each program—"Science PU College Admission," "Degree College in Arts," "JEE Coaching in [City]." Each page should be detailed and useful to parents.',
      stat: 'Websites with dedicated program pages get 3x more traffic',
      color: 'bg-amber-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Step 4: Publish Helpful Content (Blog/News)',
      description: 'Google loves fresh, helpful content. Write blog posts about topics parents care about—"How to Choose the Right PU College Stream," "CBSE vs. ICSE: Which is Better?" This builds authority and attracts organic traffic.',
      stat: 'Institutions with blogs get 55% more website visitors',
      color: 'bg-rose-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Step 5: Make Your Website Fast and Mobile-Friendly',
      description: 'Google prioritizes fast-loading, mobile-friendly websites. If your website takes more than 3 seconds to load, parents will leave. Most users are on phones—ensure your website looks perfect on every device.',
      stat: '53% of visitors leave if a site takes longer than 3 seconds to load',
      color: 'bg-purple-50'
    },
    {
      icon: <FiCheckCircle className="w-6 h-6" />,
      title: 'Step 6: Get Quality Backlinks',
      description: 'When other reputable websites link to you, Google sees this as a vote of confidence. Get listed in educational directories, local business listings, and partner with local websites to earn quality backlinks.',
      stat: 'Backlinks are among the top 3 Google ranking factors',
      color: 'bg-blue-50'
    }
  ];

  const commonMistakes = [
    {
      title: 'Ignoring Local SEO',
      description: 'Failing to include city, locality, and landmark names on your website. Parents search locally—you must appear in those searches.'
    },
    {
      title: 'No Program-Specific Pages',
      description: 'Having one generic "Admissions" page instead of dedicated pages for each program. Detailed pages rank better and convert better.'
    },
    {
      title: 'Slow Website Speed',
      description: 'A slow website loses visitors and ranks lower on Google. Speed is critical for user experience and SEO.'
    },
    {
      title: 'Not Optimizing for Mobile',
      description: 'Most parents use phones to search. If your website isn\'t mobile-friendly, you\'re losing half your potential audience.'
    },
    {
      title: 'No Fresh Content',
      description: 'A static website with no new content signals to Google that the site is inactive. Regular content updates improve rankings.'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        When parents begin their search for a school, college, or coaching centre, where do they go? Google. They type in "best schools near me," "top colleges in [city]," or "coaching for NEET in [location]." If your institution does not appear in these search results, you are invisible to these parents.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        <strong className="text-slate-900">Getting found on Google is not optional.</strong> It is essential for attracting new students. But many educational institutions don't know how to optimize their websites for search engines.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here is a step-by-step guide to getting your school, college, or coaching centre found on Google.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">70%</div>
          <div className="text-sm text-slate-500">Parents search within their city</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">3x</div>
          <div className="text-sm text-slate-500">More traffic with program pages</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">53%</div>
          <div className="text-sm text-slate-500">Leave if site loads too slow</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Step-by-Step Guide to Ranking on Google</h2>

      {/* SEO Steps */}
      {seoSteps.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">📊 {item.stat}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Common Mistakes */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Common SEO Mistakes to Avoid</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {commonMistakes.map((item, i) => (
            <div key={i} className="bg-red-50 rounded-xl p-4 border border-red-200">
              <div className="flex items-start gap-2">
                <span className="text-red-500 font-bold text-sm">✖️</span>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Good Website vs. SEO-Optimized Website</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Good Website (Not Optimized)</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Beautiful design</span></li>
              <li className="flex items-start gap-2">• <span>No keyword optimization</span></li>
              <li className="flex items-start gap-2">• <span>No local SEO</span></li>
              <li className="flex items-start gap-2">• <span>Slow loading</span></li>
              <li className="flex items-start gap-2">• <span>No fresh content</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">SEO-Optimized Website</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Beautiful design + SEO</span></li>
              <li className="flex items-start gap-2">• <span>Keyword optimized</span></li>
              <li className="flex items-start gap-2">• <span>Local SEO enabled</span></li>
              <li className="flex items-start gap-2">• <span>Fast loading</span></li>
              <li className="flex items-start gap-2">• <span>Regular blog posts</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* The SEO Checklist */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">Your SEO Checklist</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-green-50 rounded-xl p-3 border border-green-200 flex items-start gap-2">
            <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700">Research parent search keywords</span>
          </div>
          <div className="bg-green-50 rounded-xl p-3 border border-green-200 flex items-start gap-2">
            <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700">Include city and locality names</span>
          </div>
          <div className="bg-green-50 rounded-xl p-3 border border-green-200 flex items-start gap-2">
            <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700">Create program-specific pages</span>
          </div>
          <div className="bg-green-50 rounded-xl p-3 border border-green-200 flex items-start gap-2">
            <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700">Write blog posts regularly</span>
          </div>
          <div className="bg-green-50 rounded-xl p-3 border border-green-200 flex items-start gap-2">
            <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700">Make website mobile-friendly</span>
          </div>
          <div className="bg-green-50 rounded-xl p-3 border border-green-200 flex items-start gap-2">
            <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700">Speed up your website</span>
          </div>
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon is the Best Choice for SEO</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔍</div>
            <h4 className="font-bold text-slate-900 text-sm">SEO-Ready Websites</h4>
            <p className="text-xs text-slate-600">Built-in SEO with every website</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Mobile Optimized</h4>
            <p className="text-xs text-slate-600">Perfect on every device</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Fast Loading</h4>
            <p className="text-xs text-slate-600">Optimized for speed</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📝</div>
            <h4 className="font-bold text-slate-900 text-sm">Blog Integration</h4>
            <p className="text-xs text-slate-600">Easy content publishing</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Analytics</h4>
            <p className="text-xs text-slate-600">Track your performance</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔗</div>
            <h4 className="font-bold text-slate-900 text-sm">ERP + Website</h4>
            <p className="text-xs text-slate-600">All in one platform</p>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Get Found on Google with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          Getting found on Google is essential for attracting new students. Parents search online. They find institutions that appear in search results. If your institution is not visible, you are losing students to competitors who are.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon builds SEO-optimized websites for schools, colleges, and coaching centres. Mobile-friendly, fast loading, with built-in SEO. We help you rank on Google and attract more students.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for educational SEO.</strong> Get found. Get students. Grow your institution.
        </p>
      </div>
    </>
  );
};

export default EducationalSEO;